'use client';

import { c as _c } from "react/compiler-runtime";
import { format, formatDateHelper, isSameDay, UTCDate } from '@repo/sdk/date';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components/ui/tooltip';
import { useHasHydrated } from '@repo/ui/hooks';
import { Calendar, LucideIcon } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { isTicking, type PastDays, type Ticking, type TickingKind, tickingKinds, useNow } from './clock';
type DateInput = Date | null | string | undefined;

// What to show, after the props are parsed: the date or range, its kind, whether days left are
// in parentheses and how they read once past, and the format of the UTC tooltip that zone 'utc'
// adds.
type DateToShow = ({
  date: Date;
  kind: 'days-left';
  parenthesized?: boolean;
  past?: PastDays;
} | {
  date: Date;
  kind: SingleKind;
} | {
  from: Date;
  kind: RangeKind;
  to?: Date;
}) & {
  utcFormat?: FormatKind;
};
type FormatKind = 'date' | 'datetime' | 'datetime-seconds' | 'time';

// Shown with their own pattern: a day as a heading, a day without its year, a numeric date,
// a time to the second, and a day with its weekday.
const patterns = {
  'day-heading': 'd MMMM yyyy',
  'day-month': 'd MMM',
  'numeric-date': 'dd/MM/yy',
  'time-seconds': 'HH:mm:ss',
  'weekday-date': 'EEE d MMM yyyy'
};

// Shown without the year when it is the current one: a day, and a day with its weekday.
const shortPatterns = {
  'short-date': 'd MMM',
  'short-weekday-date': 'EEE d MMM'
};

// The kind decides which inputs are required. Zone 'utc' adds the UTC value in a tooltip.
// Only days left can sit in parentheses, which take the overdue colour with the text, and word
// their days once past with `past`, "3 days overdue" unless given.
type KindProps = ({
  date: DateInput;
  kind: 'days-left';
  parenthesized?: boolean;
  past?: PastDays;
} | {
  date: DateInput;
  kind: SingleKind;
  parenthesized?: never;
  past?: never;
} | {
  from: DateInput;
  kind: RangeKind;
  parenthesized?: never;
  past?: never;
  to: DateInput;
}) & {
  zone?: Zone;
};
type PatternKind = keyof typeof patterns;
type Props = KindProps & StyleProps;

// A range reads "16 Jun 2024 – 20 Jun 2024"; a compact one "Jun 16 - 20, 2024".
type RangeKind = 'compact-range' | 'range';
type ShortKind = keyof typeof shortPatterns;

// The kinds of a single date, other than days left.
type SingleKind = Exclude<FormatKind | PatternKind | ShortKind | TickingKind, 'days-left'>;
type StyleProps = {
  className?: string;
  icon?: LucideIcon;
  style?: React.CSSProperties;
};
type Zone = 'local' | 'utc';

// The UTC format of each kind's tooltip.
const utcFormats = {
  'compact-range': 'date',
  date: 'date',
  datetime: 'datetime',
  'datetime-seconds': 'datetime-seconds',
  'day-count': 'date',
  'day-heading': 'date',
  'day-month': 'date',
  days: 'date',
  'days-left': 'date',
  'numeric-date': 'date',
  range: 'date',
  relative: 'datetime',
  'relative-day': 'date',
  'short-date': 'date',
  'short-weekday-date': 'date',
  time: 'time',
  'time-seconds': 'datetime-seconds',
  'weekday-date': 'date'
} satisfies Record<DateToShow['kind'], FormatKind>;
const isPatterned = (kind: string): kind is PatternKind => Object.hasOwn(patterns, kind);
const isShort = (kind: string): kind is ShortKind => Object.hasOwn(shortPatterns, kind);
const toDate = (input: DateInput) => {
  if (!input) return undefined;
  const date = new Date(input);
  return isNaN(date.getTime()) ? undefined : date;
};
const parse = (props: KindProps): DateToShow | undefined => {
  const utcFormat = props.zone === 'utc' ? utcFormats[props.kind] : undefined;
  if ('from' in props) {
    const from = toDate(props.from);
    return from && {
      from,
      kind: props.kind,
      to: toDate(props.to),
      utcFormat
    };
  }
  const date = toDate(props.date);
  if (!date) return undefined;
  if (props.kind === 'days-left') {
    const {
      parenthesized,
      past
    } = props;
    return {
      date,
      kind: props.kind,
      parenthesized,
      past,
      utcFormat
    };
  }
  return {
    date,
    kind: props.kind,
    utcFormat
  };
};

// A range without an end, or that starts and ends on the same day, reads as that day.
const readsAsOneDay = (from: Date, to: Date | undefined) => !to || isSameDay(from, to);
const formatRange = (from: Date, to: Date | undefined, utcFormat?: FormatKind) => {
  const zoned = (date: Date) => utcFormat ? new UTCDate(date) : date;
  const fromText = formatDateHelper({
    date: from,
    utcMode: utcFormat
  });
  if (readsAsOneDay(zoned(from), to && zoned(to))) return fromText;
  return `${fromText} – ${formatDateHelper({
    date: to,
    utcMode: utcFormat
  })}`;
};

// Drops the year, then the month, from the start when the end shares them.
const formatCompactRange = (from: Date, to: Date | undefined) => {
  if (!to || readsAsOneDay(from, to)) return formatRange(from, to);
  if (from.getFullYear() !== to.getFullYear()) {
    return `${format(from, 'MMM d, yyyy')} - ${format(to, 'MMM d, yyyy')}`;
  }
  const end = from.getMonth() === to.getMonth() ? 'd, yyyy' : 'MMM d, yyyy';
  return `${format(from, 'MMM d')} - ${format(to, end)}`;
};

// Zone-independent text of a typical width, holding the local text's place during hydration.
const placeholders = {
  'compact-range': 'Dec 28 - 30, 2000',
  date: '28 Dec 2000',
  datetime: '28 Dec 2000, 20:00',
  'datetime-seconds': '28 Dec 2000, 20:00:00',
  'day-count': '10',
  'day-heading': '28 August 2000',
  'day-month': '28 Dec',
  days: '10 days',
  'days-left': '10 days left',
  'numeric-date': '28/12/00',
  range: '28 Dec 2000 – 30 Dec 2000',
  relative: '10 minutes ago',
  'relative-day': 'In 10 days',
  'short-date': '28 Dec',
  'short-weekday-date': 'Thu 28 Dec',
  time: '20:00',
  'time-seconds': '20:00:00',
  'weekday-date': 'Thu 28 Dec 2000'
} satisfies Record<DateToShow['kind'], string>;
const inParentheses = (text: string, parenthesized?: boolean) => parenthesized ? `(${text})` : text;
const placeholderOf = (shown: DateToShow) => {
  if (shown.kind === 'days-left') return inParentheses(placeholders[shown.kind], shown.parenthesized);
  if (!('from' in shown)) return placeholders[shown.kind];
  // Judged in UTC so the server and hydration agree. A range that crosses midnight in only one
  // of UTC and the local zone gets a placeholder one date too short or too long.
  const oneDay = readsAsOneDay(new UTCDate(shown.from), shown.to && new UTCDate(shown.to));
  return oneDay ? placeholders.date : placeholders[shown.kind];
};
const DateText = t0 => {
  const $ = _c(3);
  const {
    text,
    utcText
  } = t0;
  let t1;
  if ($[0] !== text || $[1] !== utcText) {
    t1 = utcText ? <Tooltip><TooltipTrigger render={<span className="select-text" />}>{text}</TooltipTrigger><TooltipContent className="select-text">{utcText} UTC</TooltipContent></Tooltip> : text;
    $[0] = text;
    $[1] = utcText;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const TickingText = t0 => {
  const $ = _c(18);
  const {
    date,
    kind,
    parenthesized,
    past,
    utcText
  } = t0;
  let t1;
  if ($[0] !== date) {
    t1 = date.getTime();
    $[0] = date;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const time = t1;
  const now = useNow(kind, time);
  const {
    overdue,
    text
  } = tickingKinds[kind];
  let t2;
  if ($[2] !== now || $[3] !== overdue || $[4] !== time) {
    t2 = cn(overdue(time, now) && "text-destructive");
    $[2] = now;
    $[3] = overdue;
    $[4] = time;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== now || $[7] !== parenthesized || $[8] !== past || $[9] !== text || $[10] !== time) {
    t3 = inParentheses(text(time, now, past), parenthesized);
    $[6] = now;
    $[7] = parenthesized;
    $[8] = past;
    $[9] = text;
    $[10] = time;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3 || $[13] !== utcText) {
    t4 = <DateText text={t3} utcText={utcText} data-sentry-element="DateText" data-sentry-source-file="date-formatter.tsx" />;
    $[12] = t3;
    $[13] = utcText;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t2 || $[16] !== t4) {
    t5 = <span className={t2} data-sentry-component="TickingText" data-sentry-source-file="date-formatter.tsx">{t4}</span>;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
const ShownDate = t0 => {
  const $ = _c(32);
  const {
    shown
  } = t0;
  const {
    utcFormat
  } = shown;
  if ("from" in shown) {
    let t1;
    if ($[0] !== shown.from || $[1] !== shown.to || $[2] !== utcFormat) {
      t1 = utcFormat && formatRange(shown.from, shown.to, utcFormat);
      $[0] = shown.from;
      $[1] = shown.to;
      $[2] = utcFormat;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    const utcText = t1;
    let t2;
    if ($[4] !== shown.from || $[5] !== shown.kind || $[6] !== shown.to) {
      t2 = shown.kind === "range" ? formatRange(shown.from, shown.to) : formatCompactRange(shown.from, shown.to);
      $[4] = shown.from;
      $[5] = shown.kind;
      $[6] = shown.to;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    const text = t2;
    let t3;
    if ($[8] !== text || $[9] !== utcText) {
      t3 = <DateText text={text} utcText={utcText} />;
      $[8] = text;
      $[9] = utcText;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  let t1;
  if ($[11] !== shown.date || $[12] !== utcFormat) {
    t1 = utcFormat && formatDateHelper({
      date: shown.date,
      utcMode: utcFormat
    });
    $[11] = shown.date;
    $[12] = utcFormat;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const utcText_0 = t1;
  if (isTicking(shown.kind)) {
    const t2 = shown.kind === "days-left" && shown.parenthesized;
    const t3 = shown.kind === "days-left" ? shown.past : undefined;
    let t4;
    if ($[14] !== shown.date || $[15] !== shown.kind || $[16] !== t2 || $[17] !== t3 || $[18] !== utcText_0) {
      t4 = <TickingText date={shown.date} kind={shown.kind} parenthesized={t2} past={t3} utcText={utcText_0} />;
      $[14] = shown.date;
      $[15] = shown.kind;
      $[16] = t2;
      $[17] = t3;
      $[18] = utcText_0;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    return t4;
  }
  if (isShort(shown.kind)) {
    const pattern = shortPatterns[shown.kind];
    const thisYear = shown.date.getFullYear() === new Date().getFullYear();
    const t2 = thisYear ? pattern : `${pattern} yyyy`;
    let t3;
    if ($[20] !== shown.date || $[21] !== t2) {
      t3 = format(shown.date, t2);
      $[20] = shown.date;
      $[21] = t2;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    let t4;
    if ($[23] !== t3 || $[24] !== utcText_0) {
      t4 = <DateText text={t3} utcText={utcText_0} />;
      $[23] = t3;
      $[24] = utcText_0;
      $[25] = t4;
    } else {
      t4 = $[25];
    }
    return t4;
  }
  let t2;
  if ($[26] !== shown.date || $[27] !== shown.kind) {
    t2 = isPatterned(shown.kind) ? format(shown.date, patterns[shown.kind]) : formatDateHelper({
      date: shown.date,
      mode: shown.kind
    });
    $[26] = shown.date;
    $[27] = shown.kind;
    $[28] = t2;
  } else {
    t2 = $[28];
  }
  const text_0 = t2;
  let t3;
  if ($[29] !== text_0 || $[30] !== utcText_0) {
    t3 = <DateText text={text_0} utcText={utcText_0} data-sentry-element="DateText" data-sentry-component="ShownDate" data-sentry-source-file="date-formatter.tsx" />;
    $[29] = text_0;
    $[30] = utcText_0;
    $[31] = t3;
  } else {
    t3 = $[31];
  }
  return t3;
};

/**
 * Renders a date as text. Local times depend on the browser's time zone, and relative ones on
 * "now", neither of which the server rendering can know, so the server renders a same-size
 * placeholder and the text follows hydration.
 */
export const DateFormatter = t0 => {
  const $ = _c(10);
  const {
    className,
    icon,
    style,
    ...props
  } = t0;
  const hasHydrated = useHasHydrated();
  const shown = parse(props);
  if (!shown) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span>-</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== className) {
    t1 = cn("inline-flex items-center gap-1", className);
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = "DateFormatter";
  const t3 = "date-formatter.tsx";
  let t4;
  if ($[3] !== icon) {
    t4 = icon && <Calendar size={16} />;
    $[3] = icon;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = hasHydrated ? <ShownDate shown={shown} /> : <span className="invisible">{placeholderOf(shown)}</span>;
  let t6;
  if ($[5] !== style || $[6] !== t1 || $[7] !== t4 || $[8] !== t5) {
    t6 = <span className={t1} style={style} data-sentry-component={t2} data-sentry-source-file={t3}>{t4}{t5}</span>;
    $[5] = style;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};

/**
 * The current time, null until hydration, then kept current by the same clock as a relative date
 * at `date`: every minute in its last hour, so a moment such as an ETA's end passing on screen
 * shows within a minute.
 */
export const useNowNear = date => {
  const $ = _c(5);
  const hasHydrated = useHasHydrated();
  let t0;
  if ($[0] !== date) {
    t0 = date?.getTime() ?? 0;
    $[0] = date;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const now = useNow("relative", t0);
  let t1;
  if ($[2] !== hasHydrated || $[3] !== now) {
    t1 = hasHydrated ? new Date(now) : null;
    $[2] = hasHydrated;
    $[3] = now;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

/**
 * Whether a due `date` has passed, counted like `kind="days-left"`: false until hydration, then
 * kept current by the same clock, so a date turning overdue on screen shows it.
 */
export const useIsOverdue = date => {
  const $ = _c(6);
  const hasHydrated = useHasHydrated();
  let t0;
  if ($[0] !== date) {
    t0 = new Date(date).getTime();
    $[0] = date;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const time = t0;
  const now = useNow("days-left", time);
  let t1;
  if ($[2] !== hasHydrated || $[3] !== now || $[4] !== time) {
    t1 = hasHydrated && tickingKinds["days-left"].overdue(time, now);
    $[2] = hasHydrated;
    $[3] = now;
    $[4] = time;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};